import { useFeature } from "@/lib/feature-management";
import type { DynamicFilterItem } from "@/store/news-feed/filters/dynamic-filters.slice";
import useNewsFeedFilters from "@/store/news-feed/filters/newsFeed-filter.slice";
import { useDynamicFilters } from "@/store/news-feed/filters/useDynamicFilters";
import { useAnalysisSidebarFilterParams } from "@/store/news-feed/params/analysis-params.context";
import NewsFeedFilterSectionWithSearch, {
  type DynamicFilterData,
} from "../NewsFeedFilterSectionWithSearch";
import {
  NarrativeClusterBulkActionMenu,
  NarrativeClusterFilterListItemMenu,
} from "./NarrativeClusterFilterMenus";

/**
 * Container component for narrative clusters filter
 * Renders filter section with search functionality for narrative clusters
 */
const NarrativeClusterFilterContainer = () => {
  const fetchOnExpand = useFeature("fetch-dynamic-filters-exp", false);

  const expandedCategories = useNewsFeedFilters(
    (state) => state.expandedCategories,
  );

  const { toggleFilterSelection, selectedFilters } =
    useAnalysisSidebarFilterParams();

  const shouldFetchNarrativeClusters =
    !fetchOnExpand ||
    expandedCategories.has("NARRATIVE_CLUSTER") ||
    (selectedFilters.get("NARRATIVE_CLUSTER")?.length ?? 0) > 0;

  const {
    items: narrativeClusters,
    isLoading: narrativeClustersLoading,
    error: narrativeClustersError,
  } = useDynamicFilters("NARRATIVE_CLUSTER", shouldFetchNarrativeClusters);
  const setCategoryExpansion = useNewsFeedFilters(
    (state) => state.setCategoryExpansion,
  );
  const isExpanded = expandedCategories.has("NARRATIVE_CLUSTER");

  const filterData: DynamicFilterData = {
    items: narrativeClusters,
    isLoading: narrativeClustersLoading,
    error: narrativeClustersError,
  };

  // Get narrative cluster management actions
  const setNarrativeClusterDeleteModalOpen = useNewsFeedFilters(
    (state) => state.setNarrativeClusterDeleteModalOpen,
  );
  const setRenameClusterModalOpen = useNewsFeedFilters(
    (state) => state.setRenameClusterModalOpen,
  );
  const setSelectedCluster = useNewsFeedFilters(
    (state) => state.setSelectedCluster,
  );

  return (
    <NewsFeedFilterSectionWithSearch
      title="Narrative Clusters"
      category="NARRATIVE_CLUSTER"
      filterData={filterData}
      selectedFilters={selectedFilters}
      onToggleFilter={toggleFilterSelection}
      visibleCount={10}
      actions={<NarrativeClusterBulkActionMenu />}
      renderItemActions={(item: DynamicFilterItem) => (
        <NarrativeClusterFilterListItemMenu
          item={item}
          setNarrativeClusterDeleteModalOpen={
            setNarrativeClusterDeleteModalOpen
          }
          setRenameClusterModalOpen={setRenameClusterModalOpen}
          setSelectedCluster={setSelectedCluster}
        />
      )}
      isExpanded={isExpanded}
      onValueChange={(value) => {
        setCategoryExpansion(
          "NARRATIVE_CLUSTER",
          value === "NARRATIVE_CLUSTER",
        );
      }}
      showCount={false}
      labelTextWrap="wrap"
    />
  );
};

export default NarrativeClusterFilterContainer;
